// The offer a foreign house has made to one of your harbours — the terms on
// one side, the two answers on the other.
import { Badge } from '@aod/ui/components/ui/badge';
import { Button } from '@aod/ui/components/ui/button';
import { Separator } from '@aod/ui/components/ui/separator';
import { Anchor } from 'lucide-react';
import { useT } from '../../i18n/index.js';
import { houseName } from '../../sea/houseNames.js';
import { STANDING_COLD, STANDING_SHUNNED } from '../../sea/merchantModel.js';
import type { SeaOffer, SeaRoute } from '../../sea/offer.js';

export type { SeaOffer, SeaRoute };
import { appDom } from '../../lib/appDom.js';

export function MerchantOfferSection({
  cityId,
  offer,
  onAccept,
  onDecline,
}: {
  cityId: string;
  offer: SeaOffer;
  onAccept: (cityId: string) => void;
  onDecline: (cityId: string) => void;
}) {
  const t = useT();
  return (
    <div
      className="space-y-2"
      data-testid="merchant-offer"
      {...appDom({
        id: 'merchant-offer',
        type: 'Section',
        scope: 'section',
        label: t('sea.offerTitle'),
      })}
    >
      <Separator />
      <div className="flex items-center gap-2">
        <Anchor className="size-4" aria-hidden />
        <span className="text-sm font-medium">{t('sea.offerTitle')}</span>
        <Badge variant="secondary">{t(houseName(offer.house))}</Badge>
      </div>
      <p className="text-xs text-muted-foreground">
        {t('sea.offerTerms', {
          house: t(houseName(offer.house)),
          gold: offer.goldPerTick,
          grain: offer.grainPerTick,
          gift: offer.gift,
        })}
      </p>
      <div className="flex gap-2">
        <Button
          type="button"
          className="flex-1"
          data-testid="merchant-offer-accept"
          {...appDom({
            id: 'merchant-offer-accept',
            type: 'Button',
            role: 'button',
            scope: 'section',
          })}
          onClick={() => onAccept(cityId)}
        >
          {t('sea.accept')}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          data-testid="merchant-offer-decline"
          {...appDom({
            id: 'merchant-offer-decline',
            type: 'Button',
            role: 'button',
            scope: 'section',
          })}
          onClick={() => onDecline(cityId)}
        >
          {t('sea.decline')}
        </Button>
      </div>
    </div>
  );
}

export function MerchantRoutesSection({
  routes,
  standing,
}: {
  routes: readonly SeaRoute[];
  /**
   * The owner's standing with the houses. `null` when the sea is not open at
   * all; a number (possibly zero) when it is.
   */
  standing?: number | null;
}) {
  const t = useT();
  // A snapshot without the field (an older save) reads as "the sea is not open".
  const stand = standing ?? null;
  if (routes.length === 0 && stand === null) return null;
  const signed = stand === null ? '' : stand > 0 ? `+${stand}` : `${stand}`;
  return (
    <div
      className="space-y-1"
      data-testid="merchant-routes"
      {...appDom({
        id: 'merchant-routes',
        type: 'Section',
        scope: 'section',
        label: t('sea.routesTitle'),
      })}
    >
      <Separator />
      <span className="text-sm font-medium">{t('sea.routesTitle')}</span>
      {routes.some((route) => route.blockaded) && (
        <p
          className="text-xs font-medium text-destructive"
          data-testid="merchant-routes-blockaded"
          {...appDom({
            id: 'merchant-routes-blockaded',
            type: 'Text',
            role: 'text',
            scope: 'section',
          })}
        >
          {t('sea.routesBlocked')}
        </p>
      )}
      {routes.map((route) => (
        <p key={`${route.house}-${route.expiresTick}`} className="text-xs text-muted-foreground">
          {t(houseName(route.house))} —{' '}
          {t('sea.route', { gold: route.goldPerTick, grain: route.grainPerTick })}
        </p>
      ))}
      {stand !== null && (
        <>
          <p className="text-xs text-muted-foreground" data-testid="sea-standing">
            {t('sea.standingTitle')}: {signed}
          </p>
          {stand <= STANDING_SHUNNED && (
            <p className="text-xs text-amber-600 dark:text-amber-400">{t('sea.standingShunned')}</p>
          )}
          {stand <= STANDING_COLD && stand > STANDING_SHUNNED && (
            <p className="text-xs text-amber-600 dark:text-amber-400">{t('sea.standingCold')}</p>
          )}
        </>
      )}
    </div>
  );
}
